<template>
    <main :class="embedded ? 'service-account-editor h-full min-h-0 bg-white' : 'relative flex flex-1 flex-col overflow-y-auto bg-stage-background'" class="billing-checkbox-scope">
        <form class="flex min-h-0 flex-1 flex-col" novalidate @submit.prevent="save">
            <div v-if="embedded" class="service-account-editor-toolbar">
                <button class="service-account-editor-close" aria-label="Detailansicht schließen" title="Detailansicht schließen" type="button" @click="$emit('close')"><svg-vue class="h-4 w-4" icon="font-awesome.regular.chevron-right"/></button>
                <h2 class="service-account-editor-title">{{ isNew ? 'Eintrag anlegen' : 'Details' }}</h2>
                <button class="service-account-editor-submit btn-stage-primary" type="submit" :disabled="loading">{{ isNew ? 'Anlegen' : 'Speichern' }}</button>
            </div>
            <div v-else class="px-8 pt-6 pb-2">
                <div class="w-full xl:w-3/4 mx-auto flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                    <h1 class="stage-page-title">{{ isNew ? 'Leistungskonto anlegen' : 'Leistungskonto bearbeiten' }}</h1>
                    <div class="inline-flex">
                        <router-link class="btn-stage-secondary mr-3" to="/workspace/service-accounts">Abbrechen</router-link>
                        <button class="btn-stage-primary" type="submit" :disabled="loading">Speichern</button>
                    </div>
                </div>
            </div>

            <div :class="embedded ? 'service-account-editor-body' : 'stage-page-body'">
                <div :class="embedded ? 'service-account-inline-sections' : 'w-full xl:w-3/4 mx-auto space-y-6'">
                    <loading :status="loading"/>

                    <div v-if="embedded && detailHolder" class="service-account-editor-profile">
                        <img
                            :alt="detailHolder.name || 'Inhaber'"
                            :class="detailHolder.type === 'user' ? 'rounded-full' : 'rounded-sm'"
                            :src="detailHolder.image || fallbackImage"
                            class="service-account-editor-profile-image"
                        >
                        <div class="min-w-0 flex-1">
                            <h2 class="service-account-editor-profile-title truncate">{{ detailHolder.name }}</h2>
                            <p class="service-account-editor-profile-subtitle truncate">{{ detailSubtitle }}</p>
                        </div>
                    </div>

                    <section class="service-account-form-section overflow-hidden bg-white px-6 pb-6 shadow sm:rounded">
                        <h3 class="-mx-6 flex h-16 items-center border-b border-ultralight stage-header-surface px-6 text-base font-semibold leading-6 text-gray-900">Kontodaten</h3>
                        <div class="mt-5 grid grid-cols-1 gap-x-5 gap-y-4 md:grid-cols-2">
                            <div class="md:col-span-2">
                                <label class="stage-filter-label block mb-2" for="service-account-name">Bezeichnung</label>
                                <input id="service-account-name" v-model.trim="form.name" class="stage-filter-input form-input block w-full text-sm sm:leading-5" maxlength="255" required>
                            </div>

                            <template v-if="isNew">
                                <div class="md:col-span-2">
                                    <label class="stage-filter-label block mb-2" for="service-account-holder-type">Kontoinhaber</label>
                                    <input-select
                                        id="service-account-holder-type"
                                        v-model="form.holder_type"
                                        class="stage-filter-select"
                                        :options="holderTypeOptions"
                                        option-label="name"
                                        required
                                        @change="holderTypeChanged"
                                    />
                                </div>

                                <div class="md:col-span-2">
                                    <label class="stage-filter-label block mb-2" for="service-account-holder">Inhaber auswählen</label>
                                    <input-select
                                        id="service-account-holder"
                                        v-model="form.holder_id"
                                        class="stage-filter-select"
                                        :options="eligibleHolders"
                                        option-label="display_label"
                                        required
                                        searchable
                                        @change="holderChanged"
                                    >
                                        <template v-slot:selectedOption="props">
                                            <div class="flex min-w-0 items-center space-x-3">
                                                <img :src="optionImage(props.option)" :alt="props.option.name || 'Inhaber'" :class="holderImageClass" class="h-6 w-6 object-cover flex-shrink-0">
                                                <span class="block truncate">{{ props.option.name }}</span>
                                            </div>
                                        </template>
                                        <template v-slot:selectOption="props">
                                            <div class="flex min-w-0 items-center space-x-3">
                                                <img :src="optionImage(props.option)" :alt="props.option.name || 'Inhaber'" :class="holderImageClass" class="h-7 w-7 object-cover flex-shrink-0">
                                                <div class="min-w-0">
                                                    <div class="block truncate text-sm text-gray-800">{{ props.option.name }}</div>
                                                    <div v-if="props.option.email" class="block truncate text-xs text-gray-500">{{ props.option.email }}</div>
                                                </div>
                                            </div>
                                        </template>
                                        <template v-slot:notFound>
                                            <div class="text-sm text-gray-500">Keine passenden Inhaber gefunden.</div>
                                        </template>
                                    </input-select>
                                </div>

                            </template>

                            <div class="md:col-span-2">
                                <label class="inline-flex items-center gap-2 text-sm text-gray-700">
                                    <input v-model="form.is_active" :disabled="item.is_operator" class="form-checkbox h-4 w-4 text-gray-700" type="checkbox">
                                    Konto aktiv
                                </label>
                                <p v-if="item.is_operator" class="mt-2 text-xs text-gray-500">Das Betreiber-Gegenkonto ist systemkritisch und kann nicht deaktiviert oder gelöscht werden.</p>
                            </div>
                        </div>
                    </section>

                    <section class="service-account-form-section overflow-hidden bg-white px-6 pb-6 shadow sm:rounded">
                        <h3 class="-mx-6 flex h-16 items-center border-b border-ultralight stage-header-surface px-6 text-base font-semibold leading-6 text-gray-900">Abrechnung</h3>
                        <div class="mt-5 grid grid-cols-1 gap-x-5 gap-y-5 md:grid-cols-2">
                            <div class="md:col-span-2">
                                <label v-if="isNew" class="stage-filter-label block mb-2" for="service-account-direction">Abrechnungsart</label>
                                <label v-else class="stage-filter-label block mb-2">Abrechnungsart</label>
                                <input-select
                                    v-if="isNew"
                                    id="service-account-direction"
                                    v-model="form.direction"
                                    class="stage-filter-select"
                                    :options="directionOptions"
                                    option-label="name"
                                    required
                                    @change="holderSelectionChanged"
                                />
                                <div v-else class="service-account-readonly-field flex min-h-[2.375rem] items-center rounded border border-bordergray-100 px-3 py-2 text-sm text-gray-600">{{ directionLabel(item.direction) }}</div>
                            </div>
                            <div v-if="!item.is_operator">
                                <label class="stage-filter-label block mb-2" for="service-account-hourly-rate">Stundensatz netto (€)</label>
                                <input id="service-account-hourly-rate" v-model="form.hourly_rate" class="stage-filter-input form-input block w-full text-sm sm:leading-5" min="0" max="999999.99" required step="0.01" type="number">
                            </div>
                            <div v-if="!item.is_operator">
                                <label class="stage-filter-label block mb-2" for="service-account-vat-rate">Umsatzsteuer (%)</label>
                                <input id="service-account-vat-rate" v-model="form.vat_rate" class="stage-filter-input form-input block w-full text-sm sm:leading-5" min="0" max="100" required step="0.01" type="number">
                            </div>
                            <div class="md:col-span-2">
                                <label class="stage-filter-label mb-2 block" for="service-account-profile">Rechnungsanschrift</label>
                                <input-select id="service-account-profile" v-model="form.billing_profile_id" class="stage-filter-select" :options="billingProfileOptions" option-label="display_label" required searchable>
                                    <template v-slot:selectedOption="props"><div class="flex min-w-0 items-center space-x-3"><img :src="props.option.holder_image || fallbackImage" :alt="props.option.holder_name || 'Inhaber'" :class="currentHolderType === 'user' ? 'rounded-full' : 'rounded-sm'" class="h-6 w-6 flex-shrink-0 object-cover"><span class="block truncate">{{ props.option.display_label }}</span></div></template>
                                    <template v-slot:selectOption="props"><div class="flex min-w-0 items-center space-x-3"><img :src="props.option.holder_image || fallbackImage" :alt="props.option.holder_name || 'Inhaber'" :class="currentHolderType === 'user' ? 'rounded-full' : 'rounded-sm'" class="h-7 w-7 flex-shrink-0 object-cover"><span class="truncate text-sm text-gray-800">{{ props.option.display_label }}</span></div></template>
                                    <template v-slot:notFound><div class="text-sm text-gray-500">Keine Rechnungsanschrift für diesen Inhaber vorhanden.</div></template>
                                </input-select>
                                <p v-if="currentHolderId && billingProfileOptions.length === 0" class="mt-2 text-xs text-red-600">Legen Sie zuerst eine <router-link class="underline" to="/workspace/billing-profiles/new">Rechnungsanschrift</router-link> für diesen Inhaber an.</p>
                            </div>
                            <div class="md:col-span-2">
                                <label class="stage-filter-label mb-2 block" for="service-account-bank-profile">Bankverbindung</label>
                                <input-select id="service-account-bank-profile" v-model="form.bank_account_id" class="stage-filter-select" :options="bankAccountOptions" option-label="display_label" searchable>
                                    <template v-slot:selectedOption="props"><div class="flex min-w-0 items-center space-x-3"><img :src="props.option.holder_image || fallbackImage" :alt="props.option.holder_name || 'Inhaber'" :class="currentHolderType === 'user' ? 'rounded-full' : 'rounded-sm'" class="h-6 w-6 flex-shrink-0 object-cover"><span class="block truncate">{{ props.option.display_label }}</span></div></template>
                                    <template v-slot:selectOption="props"><div class="flex min-w-0 items-center space-x-3"><img :src="props.option.holder_image || fallbackImage" :alt="props.option.holder_name || 'Inhaber'" :class="currentHolderType === 'user' ? 'rounded-full' : 'rounded-sm'" class="h-7 w-7 flex-shrink-0 object-cover"><span class="truncate text-sm text-gray-800">{{ props.option.display_label }}</span></div></template>
                                    <template v-slot:notFound><div class="text-sm text-gray-500">Keine Bankverbindung für diesen Inhaber vorhanden.</div></template>
                                </input-select>
                                <p class="mt-2 text-xs text-gray-500">Optional. Bankdaten werden ausschließlich im Stammdatenbereich bearbeitet.</p>
                            </div>
                        </div>
                    </section>

                    <section v-if="!item.is_operator" class="service-account-form-section overflow-hidden bg-white px-6 pb-6 shadow sm:rounded">
                        <h3 class="-mx-6 flex h-16 items-center border-b border-ultralight stage-header-surface px-6 text-base font-semibold leading-6 text-gray-900">Zugeordnete Anwender</h3>
                        <p class="mt-4 text-sm text-gray-500">{{ assignmentDescription }}</p>
                        <div class="mt-4 max-h-[32rem] overflow-y-auto rounded border border-bordergray-100">
                            <div :class="supportsCustomerApprovals ? 'sm:grid-cols-[minmax(12rem,1fr)_10.5rem_10.5rem]' : 'sm:grid-cols-1'" class="sticky top-0 z-10 hidden items-center gap-4 border-b border-ultralight bg-gray-50 px-4 py-3 text-sm text-gray-600 sm:grid">
                                <div>{{ supportsCustomerApprovals ? 'Anwender' : 'Mitarbeitende Dienstleister' }}</div>
                                <template v-if="supportsCustomerApprovals">
                                    <div class="whitespace-nowrap">Kostenfreigaben</div>
                                    <div class="whitespace-nowrap">Aktionsfreigaben</div>
                                </template>
                            </div>
                            <div v-for="(user, index) in assignableUsers" :key="user.id" :class="[{'bg-gray-50': index % 2 === 1}, supportsCustomerApprovals ? 'sm:grid-cols-[minmax(12rem,1fr)_10.5rem_10.5rem]' : 'sm:grid-cols-1']" class="grid grid-cols-[1fr_auto] items-center gap-4 border-b border-ultralight px-4 py-3 last:border-b-0">
                                <label class="flex min-w-0 items-center gap-3 cursor-pointer">
                                    <input :checked="assignment(user.id) !== null" class="form-checkbox h-4 w-4 flex-shrink-0" type="checkbox" @change="toggleUser(user, $event.target.checked)">
                                    <img :src="optionImage(user)" :alt="user.name || 'Anwender'" class="h-8 w-8 rounded-full object-cover flex-shrink-0">
                                    <span class="min-w-0"><span class="block truncate text-sm text-gray-800">{{ user.name }}</span><span class="block truncate text-xs text-gray-500">{{ user.email }}</span></span>
                                </label>
                                <div v-if="supportsCustomerApprovals" class="grid grid-cols-2 items-center gap-3 sm:grid-cols-[auto_auto_auto_auto] sm:justify-start">
                                    <span class="hidden text-xs text-gray-600 sm:inline">L</span>
                                    <label class="inline-flex items-center gap-2 text-xs text-gray-600"><input :checked="permission(user.id, 'can_read_cost_releases')" :disabled="assignment(user.id) === null" class="form-checkbox h-4 w-4" type="checkbox" @change="setPermission(user.id, 'can_read_cost_releases', $event.target.checked)"><span class="sm:hidden">Kosten lesen</span></label>
                                    <span class="hidden text-xs text-gray-600 sm:inline">S</span>
                                    <label class="inline-flex items-center gap-2 text-xs text-gray-600"><input :checked="permission(user.id, 'can_approve_cost_releases')" :disabled="assignment(user.id) === null" class="form-checkbox h-4 w-4" type="checkbox" @change="setPermission(user.id, 'can_approve_cost_releases', $event.target.checked)"><span class="sm:hidden">Kosten freigeben</span></label>
                                </div>
                                <div v-if="supportsCustomerApprovals" class="grid grid-cols-2 items-center gap-3 sm:grid-cols-[auto_auto_auto_auto] sm:justify-start">
                                    <span class="hidden text-xs text-gray-600 sm:inline">L</span>
                                    <label class="inline-flex items-center gap-2 text-xs text-gray-600"><input :checked="permission(user.id, 'can_read_action_releases')" :disabled="assignment(user.id) === null" class="form-checkbox h-4 w-4" type="checkbox" @change="setPermission(user.id, 'can_read_action_releases', $event.target.checked)"><span class="sm:hidden">Aktionen lesen</span></label>
                                    <span class="hidden text-xs text-gray-600 sm:inline">S</span>
                                    <label class="inline-flex items-center gap-2 text-xs text-gray-600"><input :checked="permission(user.id, 'can_approve_action_releases')" :disabled="assignment(user.id) === null" class="form-checkbox h-4 w-4" type="checkbox" @change="setPermission(user.id, 'can_approve_action_releases', $event.target.checked)"><span class="sm:hidden">Aktionen freigeben</span></label>
                                </div>
                            </div>
                            <div v-if="assignableUsers.length === 0" class="px-4 py-6 text-center text-sm text-gray-500">
                                {{ supportsCustomerApprovals ? 'Keine Kunden verfügbar.' : 'Keine mitarbeitenden Dienstleister dieses Unternehmens verfügbar.' }}
                            </div>
                        </div>
                    </section>

                </div>
            </div>
            <footer v-if="embedded || !isNew" class="service-account-editor-footer">
                <span v-if="lastUpdatedAt" class="service-account-editor-updated text-xs text-gray-500">
                    <img
                        v-if="lastUpdatedUser && lastUpdatedUser.image"
                        :alt="lastUpdatedUser.name"
                        :src="lastUpdatedUser.image"
                        class="service-account-editor-updated-avatar"
                    >
                    <span v-if="lastUpdatedUser">{{ lastUpdatedUser.name }}</span>
                    <span>{{ lastUpdatedUser ? 'am ' : '' }}{{ lastUpdatedAt }}</span>
                </span>
                <button v-if="!isNew" class="service-account-editor-delete btn-stage-icon-plain" aria-label="Leistungskonto löschen" title="Leistungskonto löschen" type="button" @click="deleteDialogOpen = true">
                    <svg-vue class="h-4 w-4" icon="font-awesome.trash-alt-regular"/>
                </button>
            </footer>
        </form>
        <delete-guard-dialog
            :blockers="deleteGuard.blockers"
            :deletable="canDeleteAccount"
            :loading="loading"
            :open="deleteDialogOpen"
            confirmation-text="Möchten Sie dieses Leistungskonto wirklich dauerhaft löschen? Diese Aktion kann nicht rückgängig gemacht werden."
            title="Leistungskonto löschen"
            @close="deleteDialogOpen = false"
            @confirm="remove"
        />
    </main>
</template>

<script>
import DeleteGuardDialog from '@/components/modals/delete-guard-dialog.vue';

const emptyForm = () => ({name: '', holder_type: 'company', holder_id: null, direction: 'receivable', hourly_rate: '', vat_rate: '19', billing_profile_id: null, bank_account_id: null, is_active: true, user_assignments: []});

export default {
    name: 'workspace-service-account-form',
    components: {DeleteGuardDialog},
    props: {
        embedded: {type: Boolean, default: false},
        recordId: {type: [Number, String], default: null},
    },
    data() {
        return {
            loading: false,
            item: {},
            options: {companies: [], private_customers: [], assignable_users: [], billing_profiles: [], bank_accounts: []},
            form: emptyForm(),
            holderTypeOptions: [{id: 'company', name: 'Unternehmen'}, {id: 'user', name: 'Privatkunde'}],
            fallbackImage: '/images/default/icon.png',
            deleteDialogOpen: false,
        };
    },
    computed: {
        isNew() { return this.embedded ? !this.recordId : !this.$route.params.id; },
        resolvedRecordId() { return this.embedded ? this.recordId : this.$route.params.id; },
        directionOptions() {
            const options = [{id: 'receivable', name: 'Kundenabrechnung (Einnahmen)'}];
            if (this.form.holder_type === 'company') options.push({id: 'payable', name: 'Dienstleisterabrechnung (Ausgaben)'});
            return options;
        },
        eligibleHolders() {
            const source = this.form.holder_type === 'user'
                ? (this.options.private_customers || [])
                : (this.options.companies || []).filter((company) => this.form.direction === 'receivable' ? company.is_customer_company : company.is_provider_company);
            return source.map((holder) => ({...holder, display_label: holder.name + (holder.email ? ' · ' + holder.email : '')}));
        },
        detailHolder() {
            if (!this.isNew && this.item.id) {
                return {name: this.item.holder_name, image: this.item.holder_image, type: this.item.holder_type};
            }
            const holder = this.eligibleHolders.find((entry) => Number(entry.id) === Number(this.form.holder_id));
            return holder ? {name: holder.name, image: holder.image, type: this.form.holder_type} : null;
        },
        detailSubtitle() { return String(this.form.name || 'Leistungskonto').trim(); },
        holderImageClass() { return this.form.holder_type === 'user' ? 'rounded-full' : 'rounded-sm'; },
        currentHolderType() { return this.isNew ? this.form.holder_type : this.item.holder_type; },
        currentHolderId() { return Number(this.isNew ? this.form.holder_id : this.item.holder_id) || null; },
        currentDirection() { return this.isNew ? this.form.direction : this.item.direction; },
        supportsCustomerApprovals() { return this.currentDirection === 'receivable'; },
        assignableUsers() {
            const users = this.options.assignable_users || [];
            if (this.currentDirection === 'payable') {
                return users.filter((user) => user.assignment_role === 'service_provider' && Number(user.company_id) === this.currentHolderId);
            }
            if (this.currentDirection === 'receivable') {
                return users.filter((user) => user.assignment_role === 'customer');
            }
            return [];
        },
        assignmentDescription() {
            return this.supportsCustomerApprovals
                ? 'Nur hier zugeordnete Kunden können Positionen dieses Leistungskontos sehen. Kosten- und Aktionsfreigaben werden kontobezogen vergeben.'
                : 'Zugeordnete mitarbeitende Dienstleister können dieses Leistungskonto für ihre Leistungen und Ausgaben verwenden.';
        },
        billingProfileOptions() {
            return (this.options.billing_profiles || []).filter((profile) => profile.holder_type === this.currentHolderType && Number(profile.holder_id) === this.currentHolderId);
        },
        bankAccountOptions() {
            return (this.options.bank_accounts || []).filter((account) => account.holder_type === this.currentHolderType && Number(account.holder_id) === this.currentHolderId);
        },
        deleteGuard() {
            return this.item.delete_guard || {deletable: false, is_operator: false, blockers: [], references: {}};
        },
        canDeleteAccount() { return this.deleteGuard.deletable === true; },
        lastUpdatedAt() {
            if (this.isNew || !this.item.updated_at) return '';
            return this.$momentFormatDateTime(this.item.updated_at).replace(/\s+(\d{1,2}:\d{2})$/, ', $1');
        },
        lastUpdatedUser() { return this.item.updated_by_user || null; },
    },
    watch: {
        recordId() { if (this.embedded) { this.deleteDialogOpen = false; this.item = {}; this.form = emptyForm(); this.load(); } },
    },
    mounted() { this.load(); },
    methods: {
        load() {
            this.loading = true;
            const url = this.isNew ? 'api/workspace/admin/service-accounts' : 'api/workspace/admin/service-accounts/' + this.resolvedRecordId;
            axios.get(url).then((response) => {
                this.options = response.data.options || this.options;
                if (!this.isNew) {
                    this.item = response.data.item || {};
                    this.form = {...emptyForm(), ...this.item, user_assignments: (this.item.user_assignments || []).map((assignment) => ({...assignment}))};
                }
            }).finally(() => { this.loading = false; });
        },
        save() {
            this.loading = true;
            const creating = this.isNew;
            const payload = {
                name: this.form.name,
                hourly_rate: this.form.hourly_rate,
                vat_rate: this.form.vat_rate,
                billing_profile_id: Number(this.form.billing_profile_id || 0) || null,
                bank_account_id: Number(this.form.bank_account_id || 0) || null,
                is_active: !!this.form.is_active,
                user_assignments: this.form.user_assignments.map((assignment) => this.supportsCustomerApprovals
                    ? assignment
                    : {
                        ...assignment,
                        can_read_cost_releases: false,
                        can_approve_cost_releases: false,
                        can_read_action_releases: false,
                        can_approve_action_releases: false,
                    }),
            };
            if (creating) Object.assign(payload, {holder_type: this.form.holder_type, holder_id: Number(this.form.holder_id), direction: this.form.direction});
            const request = creating ? axios.post('api/workspace/admin/service-accounts', payload) : axios.put('api/workspace/admin/service-accounts/' + this.item.id, payload);
            request.then((response) => {
                this.$notify({title: 'Erfolg', text: response.data.message || 'Daten gespeichert.', type: 'success'});
                this.options = response.data.options || this.options;
                this.item = response.data.item || this.item;
                this.form = {...this.form, ...this.item, user_assignments: (this.item.user_assignments || []).map((assignment) => ({...assignment}))};
                this.$emit('saved', this.item);
                if (!this.embedded && creating && this.item.id) this.$router.replace('/workspace/service-accounts/' + this.item.id + '/edit');
            }).finally(() => { this.loading = false; });
        },
        holderTypeChanged() { this.form.holder_id = null; this.form.user_assignments = []; if (this.form.holder_type === 'user') this.form.direction = 'receivable'; this.resetMasterData(); },
        holderSelectionChanged() { this.form.holder_id = null; this.form.user_assignments = []; this.resetMasterData(); },
        holderChanged() {
            this.form.user_assignments = [];
            this.resetMasterData();
            const defaultProfile = (this.options.billing_profiles || []).find((profile) => profile.holder_type === this.currentHolderType && Number(profile.holder_id) === this.currentHolderId && profile.is_default);
            const defaultBank = (this.options.bank_accounts || []).find((account) => account.holder_type === this.currentHolderType && Number(account.holder_id) === this.currentHolderId && account.is_default);
            if (defaultProfile) this.form.billing_profile_id = defaultProfile.id;
            if (defaultBank) this.form.bank_account_id = defaultBank.id;
        },
        resetMasterData() {
            this.form.billing_profile_id = null;
            this.form.bank_account_id = null;
        },
        optionImage(option) { return option && option.image ? option.image : this.fallbackImage; },
        remove() {
            if (!this.canDeleteAccount || this.loading) return;
            this.loading = true;
            const removedId = this.item.id;
            axios.delete('api/workspace/admin/service-accounts/' + removedId).then(() => {
                this.deleteDialogOpen = false;
                this.$emit('deleted', removedId);
                if (!this.embedded) this.$router.push('/workspace/service-accounts');
            }).catch((error) => {
                const guard = error.response && error.response.status === 422 && error.response.data && error.response.data.delete_guard;
                if (guard) this.item = {...this.item, delete_guard: guard};
            }).finally(() => { this.loading = false; });
        },
        assignment(userId) { return this.form.user_assignments.find((assignment) => Number(assignment.user_id) === Number(userId)) || null; },
        permission(userId, key) { const value = this.assignment(userId); return value ? value[key] === true : false; },
        toggleUser(user, checked) {
            this.form.user_assignments = checked
                ? [...this.form.user_assignments, {
                    user_id: Number(user.id),
                    can_read_cost_releases: false,
                    can_approve_cost_releases: false,
                    can_read_action_releases: false,
                    can_approve_action_releases: false,
                }]
                : this.form.user_assignments.filter((assignment) => Number(assignment.user_id) !== Number(user.id));
        },
        setPermission(userId, key, checked) {
            this.form.user_assignments = this.form.user_assignments.map((assignment) => {
                if (Number(assignment.user_id) !== Number(userId)) return assignment;
                const updated = {...assignment, [key]: checked};
                if (key === 'can_approve_cost_releases' && checked) updated.can_read_cost_releases = true;
                if (key === 'can_read_cost_releases' && !checked) updated.can_approve_cost_releases = false;
                if (key === 'can_approve_action_releases' && checked) updated.can_read_action_releases = true;
                if (key === 'can_read_action_releases' && !checked) updated.can_approve_action_releases = false;
                return updated;
            });
        },
        directionLabel(direction) { return {receivable: 'Kundenabrechnung (Einnahmen)', payable: 'Dienstleisterabrechnung (Ausgaben)', settlement: 'Betreiber-Gegenkonto (Verrechnung)'}[direction] || direction; },
    },
};
</script>

<style scoped>
.service-account-editor {
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.service-account-readonly-field {
    background: var(--ui-surface-raised);
}

.service-account-editor-toolbar {
    display: flex;
    height: 3.25rem;
    min-height: 3.25rem;
    align-items: center;
    gap: 0.75rem;
    flex-shrink: 0;
    padding-left: 1rem;
    border-bottom: 1px solid var(--border-ultralight);
    background: var(--ui-surface-raised);
}

.service-account-editor-title {
    display: flex;
    min-width: 0;
    align-items: center;
    gap: 0.75rem;
    overflow: hidden;
    color: var(--ui-text-strong);
    font-size: 0.875rem;
    font-weight: 500;
    line-height: 1.25rem;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.service-account-editor-submit {
    align-self: stretch;
    min-width: 8rem;
    margin-left: auto;
    border: 0;
    border-radius: 0;
}

.service-account-editor-close {
    display: none;
    width: 2rem;
    height: 2rem;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    color: var(--ui-text-subtle);
}

.service-account-editor-body {
    position: relative;
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 1.5rem 1.75rem 2rem;
    background: #fff;
}

.service-account-editor-profile {
    display: flex;
    min-height: 4.75rem;
    align-items: flex-start;
    gap: 1rem;
    margin-bottom: 1.5rem;
}

.service-account-editor-profile-image {
    width: 4.5rem;
    height: 4.5rem;
    flex: 0 0 4.5rem;
    object-fit: cover;
}

.service-account-editor-profile-title {
    color: var(--ui-text-strong);
    font-size: 1.25rem;
    font-weight: 600;
    line-height: 1.25;
}

.service-account-editor-profile-subtitle {
    margin-top: 0.125rem;
    color: var(--ui-text-disabled);
    font-size: 0.875rem;
    line-height: 1rem;
}

.service-account-editor-footer {
    display: flex;
    height: 3.5rem;
    min-height: 3.5rem;
    flex-shrink: 0;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 0 1.5rem;
    background: var(--ui-surface-raised);
    box-shadow: inset 0 1px 0 var(--border-ultralight);
}

.service-account-editor-updated {
    display: inline-flex;
    min-width: 0;
    align-items: center;
    gap: 0.375rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.service-account-editor-updated-avatar {
    width: 1.25rem;
    height: 1.25rem;
    flex-shrink: 0;
    border-radius: 9999px;
    object-fit: cover;
}

.service-account-editor-delete {
    color: var(--ui-text-subtle);
}

.service-account-editor-delete:hover,
.service-account-editor-delete:focus {
    color: var(--ui-text-default);
}

.service-account-editor .service-account-form-section {
    overflow: visible;
    padding: 0;
    border: 0;
    border-radius: 0;
    background: transparent;
    box-shadow: none;
}

.service-account-editor .service-account-form-section + .service-account-form-section {
    margin-top: 1.75rem;
}

.service-account-editor .service-account-form-section > h3 {
    display: block;
    height: auto;
    margin: 0 0 0.75rem;
    padding: 0;
    border: 0;
    background: transparent;
    color: var(--ui-text-strong);
    font-size: 1rem;
    font-weight: 600;
    line-height: 1.5rem;
}

.service-account-editor .service-account-form-section > h3 + div,
.service-account-editor .service-account-form-section > h3 + p {
    margin-top: 0;
}

@media (max-width: 1279px) {
    .service-account-editor-close {
        display: inline-flex;
    }
}

@media (max-width: 639px) {
    .service-account-editor-toolbar {
        height: 3.25rem;
        min-height: 3.25rem;
        padding-left: 0.75rem;
    }

    .service-account-editor-body {
        padding: 0.75rem;
    }
}
</style>
